import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'inverse';
export type ButtonSize = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-semibold no-underline ' +
  'transition-[background-color,border-color,color,box-shadow,transform] duration-micro ease-emphasis ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-surface disabled:pointer-events-none disabled:opacity-55 active:translate-y-px ' +
  'hover:no-underline';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-contrast shadow-subtle hover:bg-primary-hover',
  secondary: 'border border-line-strong bg-surface text-ink hover:border-primary hover:text-primary',
  ghost: 'text-ink hover:bg-raised',
  danger: 'bg-danger text-white hover:brightness-110',
  inverse: 'bg-white text-ink hover:bg-white/90',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-sm',
  md: 'h-11 px-5 text-[0.9375rem]',
  lg: 'h-13 px-7 text-base',
};

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  children?: ReactNode;
}

export type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

/**
 * Always renders a real `<button>`. Loading state keeps the accessible name and marks the control
 * busy rather than swapping the label out, so screen-reader users are not left without context.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', fullWidth, loading, iconLeft, iconRight, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading ? <Spinner /> : iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
});

export interface ButtonLinkProps extends CommonProps {
  to: string;
  external?: boolean;
  'aria-label'?: string;
  /** Useful for closing a menu or drawer as the link navigates. */
  onClick?: () => void;
}

/**
 * A link that looks like a button. Internal destinations use the router; external ones get
 * `rel="noopener noreferrer"` automatically.
 */
export function ButtonLink({
  to,
  external,
  variant = 'primary',
  size = 'md',
  fullWidth,
  iconLeft,
  iconRight,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  const classes = cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className);
  const isExternal = external ?? /^(https?:)?\/\//.test(to) ?? false;

  if (isExternal) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={classes} {...rest}>
        {iconLeft}
        {children}
        {iconRight}
      </a>
    );
  }

  return (
    <Link to={to} className={classes} {...rest}>
      {iconLeft}
      {children}
      {iconRight}
    </Link>
  );
}
